/**
 * Chart section with D1 (Rashi) and D9 (Navamsa) chart display side-by-side
 */

import React, { useMemo } from 'react';
import { Box, Card, Typography } from '@mui/joy';
import { ReportSection } from '../ReportSection';
import type { ReportSectionProps } from '../types';
import AdvancedNorthIndianChart from '../../AdvancedNorthIndianChart';

interface ChartSectionProps extends ReportSectionProps {
  astroData?: any;
  vargaMap?: any;
}

export const ChartSection: React.FC<ChartSectionProps> = ({ data, astroData, vargaMap, onNavigate }) => {
  // Construct chartData for AdvancedNorthIndianChart from astroData
  const chartData = useMemo(() => {
    if (!astroData) return null;

    const planetNames = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Rahu', 'Ketu'];
    const planets = planetNames.map(name => {
      const graha = name !== 'Rahu' && name !== 'Ketu' 
        ? astroData.grahas?.[name] 
        : null;
      const node = (name === 'Rahu' || name === 'Ketu') 
        ? astroData.nodes?.[name] 
        : null;
      const placement = astroData.placements?.[name];

      if (!placement) return null;

      const lon = graha?.lon ?? node ?? 0;
      const degree = lon % 30;
      const rasi = Math.floor(lon / 30) + 1;

      // Get dignity using sign-based check — matches App.tsx getDignity/exalt flag
      // (Sign-based: any planet in its exaltation sign is Exalted, not just near exact degree)
      const grahaLon = graha?.lon ?? node ?? 0;
      const EXALT_S: Record<string, number> = { Sun:0, Moon:1, Mars:9, Mercury:5, Jupiter:3, Venus:11, Saturn:6 };
      const DEBIL_S: Record<string, number> = { Sun:6, Moon:7, Mars:3, Mercury:11, Jupiter:9, Venus:5, Saturn:0 };
      const signIdx0 = Math.floor(((grahaLon % 360) + 360) % 360 / 30);
      const dignityRaw = (name !== 'Rahu' && name !== 'Ketu' && graha)
        ? (EXALT_S[name] === signIdx0 ? 'exalted' : DEBIL_S[name] === signIdx0 ? 'debilitated' : 'neutral')
        : 'neutral';

      return {
        name: name as any,
        house: placement.house,
        rasi: rasi,
        degree: degree,
        nakshatra: graha?.nakshatra || '',
        nakshatraPada: graha?.pada || 1,
        retro: graha?.retrograde || false,
        combust: graha?.combust || false,
        exalted: dignityRaw === 'exalted',
        debilitated: dignityRaw === 'debilitated',
        longitude: lon,
      };
    }).filter(Boolean);

    return {
      planets: planets as any[],
      ascendant: {
        rasi: astroData.asc?.ascendantSignIndex || 1,
        degree: astroData.asc?.ascendant || 0,
      },
      aspects: astroData.aspects || [],
    };
  }, [astroData]);

  // Build D9 (Navamsa) chartData from vargaMap
  const d9ChartData = useMemo(() => {
    if (!vargaMap?.D9 || !astroData) return null;
    const d9 = vargaMap.D9;
    const ascSign: number = d9.ascSign || 1;
    const planetSigns: Record<string, number> = d9.planetSigns || {};

    const EXALT_S: Record<string, number> = { Sun:0, Moon:1, Mars:9, Mercury:5, Jupiter:3, Venus:11, Saturn:6 };
    const DEBIL_S: Record<string, number> = { Sun:6, Moon:7, Mars:3, Mercury:11, Jupiter:9, Venus:5, Saturn:0 };

    const planetNames = ['Sun','Moon','Mercury','Venus','Mars','Jupiter','Saturn','Rahu','Ketu'];
    const planets = planetNames.map(name => {
      const signIdx1: number = planetSigns[name];
      if (!signIdx1) return null;
      const house = ((signIdx1 - ascSign + 12) % 12) + 1;
      const signIdx0 = signIdx1 - 1;
      const exalted = (name !== 'Rahu' && name !== 'Ketu') ? EXALT_S[name] === signIdx0 : false;
      const debilitated = (name !== 'Rahu' && name !== 'Ketu') ? DEBIL_S[name] === signIdx0 : false;
      const graha = astroData.grahas?.[name];
      return {
        name: name as any,
        house,
        rasi: signIdx1,
        degree: 15,
        nakshatra: '',
        nakshatraPada: 1,
        retro: graha?.retrograde || false,
        combust: false,
        exalted,
        debilitated,
        longitude: signIdx0 * 30 + 15,
      };
    }).filter(Boolean);

    return {
      planets: planets as any[],
      ascendant: { rasi: ascSign, degree: 0 },
      aspects: [],
    };
  }, [vargaMap, astroData]);

  return (
    <ReportSection
      title="Natal Charts — D1 (Rāśī) & D9 (Navāṃśa)"
      icon="📊"
      pageBreakBefore
      onNavigate={onNavigate ? () => onNavigate('calculator') : undefined}
      navigateLabel="View Live Chart →"
      navigateTab="calculator"
    >
      <Box sx={{ mb: 2 }}>
        {/* Person name — shown once at the top */}
        <Typography level="title-md" sx={{ textAlign: 'center', fontWeight: 700, mb: 2 }}>
          {data.birthDetails.name}
        </Typography>

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: d9ChartData ? { xs: '1fr', sm: '1fr 1fr' } : '1fr',
          gap: 2,
          '@media print': { gridTemplateColumns: d9ChartData ? '1fr 1fr' : '1fr' },
        }}>
          {/* D1 Chart */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {chartData ? (
              <AdvancedNorthIndianChart
                chartData={chartData}
                size={d9ChartData ? 320 : 420}
                showExport={false}
              />
            ) : (
              <Card variant="outlined" sx={{ p: 4, border: '2px dashed', borderColor: 'neutral.300' }}>
                <Typography level="body-md" sx={{ color: 'text.secondary' }}>Chart data unavailable</Typography>
              </Card>
            )}
            <Typography level="title-sm" sx={{ mt: 1, fontWeight: 700, color: 'primary.main' }}>
              D1 — Rāśī (Birth Chart)
            </Typography>
          </Box>

          {/* D9 Chart */}
          {d9ChartData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <AdvancedNorthIndianChart
                chartData={d9ChartData}
                size={320}
                showExport={false}
              />
              <Typography level="title-sm" sx={{ mt: 1, fontWeight: 700, color: 'primary.main' }}>
                D9 — Navāṃśa (Soul Chart)
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Chart interpretation */}
      <Card variant="soft" sx={{ p: 2, mt: 2 }}>
        <Typography level="title-sm" sx={{ mb: 1, color: 'primary.main' }}>
          Chart Overview
        </Typography>
        <Typography level="body-sm" sx={{ lineHeight: 1.6, mb: 1 }}>
          The <strong>D1 (Rāśī)</strong> chart is the foundational birth chart in Vedic astrology — it maps
          every planet's position at the moment of birth and governs all areas of life. The Ascendant (Lagna)
          is <strong>{data.birthDetails.ascendant}</strong>, ruled by{' '}
          <strong>{data.birthDetails.ascendantLord}</strong>, shaping the core personality and life path.
        </Typography>
        {d9ChartData && (
          <Typography level="body-sm" sx={{ lineHeight: 1.6 }}>
            The <strong>D9 (Navāṃśa)</strong> chart is the most important divisional chart — it reveals the
            soul's deeper purpose, the quality of marriage and partnerships, and the inner strength of each
            planet. A planet that is weak in D1 but strong in D9 retains its ability to deliver results; a
            planet strong in D1 but weak in D9 may not fully manifest its promise. Reading D1 and D9 together
            gives the complete picture of natal potential.
          </Typography>
        )}
      </Card>
    </ReportSection>
  );
};
